import React, { useMemo, useState } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import { useFilterStore } from "../../../store/filterStore";

interface Column {
  key: string;
  label: string;
  align?: "left" | "right" | "center";
  width?: string;
  sortable?: boolean;
}

interface InstanceTableHeaderProps {
  sortKey: string | null;
  sortOrder: "asc" | "desc" | null;
  onSort: (key: string) => void;
}

// assuming this comes from your Zustand store or props

const InstanceTableHeader: React.FC<InstanceTableHeaderProps> = ({
  sortKey,
  sortOrder,
  onSort,
}) => {
  const [hovered, setHovered] = useState<string | null>(null);
  const timeUnit = useFilterStore((state) => state.timeUnit);
  const columnVisibility = useFilterStore((state) => state.columnVisibility);

  const getColumns = (): Column[] => {
    // Utility function to map time unit → display label
    const getPriceLabel = (provider: string) =>
      `${provider} Price (${timeUnit || "Per Hour"})`;

    const allColumns: Column[] = [
      { key: "checkbox", label: "", width: "w-12" },
      { key: "instanceType", label: "Instance Type", sortable: true },
      { key: "instanceFamily", label: "Instance Family", sortable: true },
      { key: "vcpus", label: "vCPUs", sortable: true },
      { key: "memory", label: "Memory (GB)", sortable: true },
      { key: "cpuArchitecture", label: "CPU Architecture", sortable: true },
      { key: "gpu", label: "GPU (GB)", sortable: true },
      { key: "gpuName", label: "GPU Name", sortable: true },
      { key: "aws", label: getPriceLabel("AWS"), sortable: true },
      { key: "gcp", label: getPriceLabel("GCP"), sortable: true },
      { key: "azure", label: getPriceLabel("Azure"), sortable: true },
      { key: "best", label: "Best" },
      { key: "actions", label: "", width: "w-12" },
    ];

    // Filter columns based on visibility (always show checkbox and actions)
    return allColumns.filter((col) => {
      if (col.key === "checkbox" || col.key === "actions") return true;
      return columnVisibility[col.key as keyof typeof columnVisibility] !== false;
    });
  };
  const columns = getColumns();
  return (
    <thead className="bg-gray-50 text-gray-600 uppercase text-xs font-medium">
      <tr>
        {columns.map(({ key, label, align = "left", width, sortable }) => {
          const isActive = sortKey === key;
          const isAsc = sortOrder === "asc";
          const showIcon = hovered === key || isActive;

          return (
            <th
              key={key}
              className={`px-6 py-6 text-${align} ${
                width ?? ""
              } select-none cursor-pointer relative`}
              onMouseEnter={() => setHovered(key)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => sortable && onSort(key)}
            >
              <div className="flex items-center justify-between">
                <span className="truncate">{label}</span>

                {sortable && (
                  <span
                    className={`ml-1 w-4 h-4 flex items-center justify-center transition-all duration-150`}
                  >
                    {isActive ? (
                      isAsc ? (
                        <ChevronUp size={14} className="text-gray-600" />
                      ) : (
                        <ChevronDown size={14} className="text-gray-600" />
                      )
                    ) : showIcon ? (
                      <ChevronUp size={14} className="text-gray-400" />
                    ) : (
                      <ChevronUp size={14} className="opacity-0" />
                    )}
                  </span>
                )}
              </div>
            </th>
          );
        })}
      </tr>
    </thead>
  );
};

export default InstanceTableHeader;
